import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Star, Quote } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function ReviewSlider() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const intervalRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reviews/approved`);
      setReviews(res.data.reviews || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const scrollNext = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 320 + 16; // card w + gap
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (el.scrollLeft >= maxScroll - 10) {
      el.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      el.scrollBy({ left: cardWidth, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (reviews.length <= 1 || isPaused) {
      clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(scrollNext, 4000);
    return () => clearInterval(intervalRef.current);
  }, [reviews, isPaused, scrollNext]);

  if (loading) return null;

  if (reviews.length === 0) {
    return (
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="reviews-empty">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-white/30 text-sm">Be the first to leave a review</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="reviews-slider-section">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF4500]">Reviews</span>
          <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white mt-2">
            Trusted by Creatives
          </h2>
        </div>

        {/* Slider */}
        <div
          ref={scrollRef}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          data-testid="reviews-scroll-container"
        >
          {reviews.map((review, idx) => (
            <ReviewCard key={review.id || idx} review={review} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ReviewCard({ review, idx }) {
  return (
    <div
      className="flex-shrink-0 w-[300px] sm:w-[320px] bg-[#0c0c10] border border-white/[0.06] rounded-2xl p-6 hover:border-white/[0.12] transition-colors"
      data-testid={`review-card-${idx}`}
    >
      {/* Quote icon */}
      <Quote className="w-5 h-5 text-[#FF4500]/40 mb-3" />

      {/* Comment */}
      <p className="text-white/70 text-sm leading-relaxed mb-5 line-clamp-4">
        {review.comment}
      </p>

      {/* Bottom row */}
      <div className="flex items-center justify-between">
        {/* Stars */}
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`w-3.5 h-3.5 ${
                star <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-white/10'
              }`}
            />
          ))}
        </div>

        {/* Label - show profession */}
        <span className="text-xs text-white/35 font-medium">
          {review.profession || review.user_label || 'Creative'}
        </span>
      </div>

      {/* Verified badge for seed/paid */}
      {(review.source === 'seed' || review.plan_type) && (
        <div className="mt-3 pt-3 border-t border-white/[0.04]">
          <span className="text-[10px] uppercase tracking-wider text-emerald-400/60 font-semibold">
            Verified User
          </span>
        </div>
      )}
    </div>
  );
}
